/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { get, forEach } from 'lodash';
import { Grid, Col, Row } from 'react-bootstrap';
import queryString from 'qs';

import config from '../../config/filters/homepage-shows';
import moviesConfig from '../../config/filters/homepage-movies';
import HomePageFilters from './HomePageFilters';
import { getRangeFromDays } from '../../utils/dates';
import { serializeFilters } from '../../utils/filters';

import './Homepage.css';
import HomePageShows from './HomePage-Shows';
import HomePageMovies from './HomePage-Movies';

const getInitialCountryValues = allowedDataCategories => {
  let value;
  forEach(allowedDataCategories, restrictionString => {
    const [restrictedField, restriction] = restrictionString.split(':');
    if (restrictedField === 'country' && restriction !== '*') {
      const values = restriction.split(',').map(v => ({ label: v, value: v }));

      value = values.length > 1 ? values : values[0];
    }
  });
  return value;
};

const initialState = (allowedPages, allowedDataCategories) => ({
  country: getInitialCountryValues(allowedDataCategories),
  contentType: allowedPages.includes('/shows')
    ? { label: 'Shows only', value: 'shows' }
    : { label: 'Movies only', value: 'movies' }
});

const HomePage = ({
  showQueries,
  movieQueries,
  user: { username, allowedPages, allowedDataCategories },
  filters,
  queryActions: { runInlineQueriesForPage },
  filtersActions: { updateFilters }
}) => {
  const [stateFilters, setStateFilters] = useState(
    initialState(allowedPages, allowedDataCategories)
  );
  const [appliedFilters, setAppliedFilters] = useState(
    initialState(allowedPages, allowedDataCategories)
  );

  const getFiltersConfig = () => {
    const countryConfig = get(filters, ['config', 'country'], {});
    const country = { ...countryConfig, value: stateFilters.country };

    const contentOptions = [];

    if (allowedPages.includes('/shows')) {
      contentOptions.push({ label: 'Shows only', value: 'shows' });
    }
    if (allowedPages.includes('/movies')) {
      contentOptions.push({ label: 'Movies only', value: 'movies' });
    }

    const contentType = {
      id: 'contentType',
      label: 'Content Type',
      options: contentOptions,
      isMulti: false,
      value: stateFilters.contentType,
      hideActiveFilters: true
    };

    return [country, contentType];
  };

  const updateQueries = (content = 'shows') => {
    runInlineQueriesForPage(content === 'shows' ? config : moviesConfig, {
      daysInPast: { id: 'daysInPast', value: { value: '7' } },
      contentType: {
        id: 'contentType',
        value: { value: content === 'shows' ? 'series' : 'movie' }
      },
      country: {
        ...get(filters, ['config', 'country'], {
          id: 'country'
        }),
        value: stateFilters.country
      },
      releaseDate: { id: 'releaseDate', value: { value: '-TBD' } }
    });
  };

  const handleFilterChange = (id, value) => {
    if (id === 'contentType') {
      setStateFilters({ ...stateFilters, contentType: value });
    } else {
      setStateFilters({
        ...stateFilters,
        [id]: value
      });
    }
  };

  const handleClear = id => {
    if (id === 'contentType') {
      return null;
    }

    return setStateFilters({ ...stateFilters, [id]: null });
  };

  const handleClearAllFromActive = filterId => () => handleClear(filterId);

  useEffect(() => {
    updateQueries(stateFilters.contentType.value);
    document.title = 'Home | CVM Insights';
  }, []);

  const applyFilters = () => {
    updateFilters(stateFilters, { syncWithUrl: false });
    setAppliedFilters(stateFilters);
    updateQueries(stateFilters.contentType.value);
  };

  const applyClear = () => {
    setStateFilters(appliedFilters);
  };

  const formatFiltersForURL = (includeDate = false) => {
    const obj = Object.assign({}, filters.fields);
    if (includeDate && filters.fields && filters.fields.date) {
      obj.date.value = { value: getRangeFromDays(7).value };
    }

    if (obj.country && obj.country.value && !obj.country.id) {
      obj.country.id = 'country';
    }

    const filtersQuery = serializeFilters(obj);

    return queryString.stringify({ filters: filtersQuery });
  };

  return filters.config ? (
    <section className="tvt-main__content homepage-main">
      <Grid fluid componentClass="section">
        <Row>
          <Col md={12}>
            <header className="tvt-main__header">
              <div className="page-header-root">
                <h1 className="page-header__title">{`Hello, ${username}`}</h1>
              </div>
              <div className="help-page__body">
                Here is your data at a glance
              </div>
            </header>
          </Col>
        </Row>
        <HomePageFilters
          filters={getFiltersConfig()}
          appliedFilters={appliedFilters}
          stateFilters={stateFilters}
          handleFilterChange={handleFilterChange}
          applyFunc={applyFilters}
          applyClearFunc={applyClear}
          onClearFunc={handleClear}
          onClearAllFunc={handleClearAllFromActive}
        />
        {appliedFilters.contentType.value === 'shows' ? (
          <HomePageShows
            queries={showQueries}
            formatFiltersForURL={formatFiltersForURL}
          />
        ) : (
          <HomePageMovies
            queries={movieQueries}
            formatFiltersForURL={formatFiltersForURL}
          />
        )}
      </Grid>
    </section>
  ) : null;
};

HomePage.propTypes = {
  queryActions: PropTypes.shape({
    runInlineQueriesForPage: PropTypes.func.isRequired
  }).isRequired,
  filtersActions: PropTypes.shape({
    updateFilters: PropTypes.func.isRequired
  }).isRequired,
  showQueries: PropTypes.shape({
    trendingActors: PropTypes.shape({}),
    trendingShows: PropTypes.shape({}),
    topPerformingShows: PropTypes.shape({}),
    topPerformingShowsBinge: PropTypes.shape({}),
    homepageShowAnticipation: PropTypes.shape({})
  }).isRequired,
  movieQueries: PropTypes.shape({
    trendingActors: PropTypes.shape({}),
    trendingMovies: PropTypes.shape({}),
    topPerformingMoviess: PropTypes.shape({}),
    homepageMovieAnticipation: PropTypes.shape({})
  }).isRequired,
  user: PropTypes.shape({
    username: PropTypes.string.isRequired,
    allowedPages: PropTypes.array.isRequired,
    allowedDataCategories: PropTypes.array.isRequired
  }).isRequired,
  filters: PropTypes.shape({
    config: PropTypes.shape([]),
    fields: PropTypes.shape({
      date: PropTypes.shape([])
    })
  }).isRequired
};

export default HomePage;
